import { AnalyticsBrowser } from "@segment/analytics-next";

import { EventBus } from "./bus";
import { Environment } from "./types/environments";

const stagingWriteKey = import.meta.env.VITE_SEGMENT_STAGING_WRITE_KEY;
const productionWriteKey = import.meta.env.VITE_SEGMENT_PRODUCTION_WRITE_KEY;

const EnvironmentWriteKey: Record<Environment, string> = {
  live: productionWriteKey,
  sandbox: "", // we explicitly don't track events in sandbox as not useful,
  "live-staging": stagingWriteKey,
  "sandbox-staging": stagingWriteKey,
  local: stagingWriteKey,
};

// A no-op analytics implementation for environments where tracking is disabled
// or if analytics is disabled in the config
const noopAnalytics = {
  track: () => Promise.resolve({ isSent: false }),
  identify: () => Promise.resolve({ isSent: false }),
  page: () => Promise.resolve({ isSent: false }),
};

export type AnalyticsInstance = ReturnType<typeof createAnalyticsInstance>;

export const createAnalyticsInstance = (
  analyticsEnabled: boolean,
  environment: Environment
) => {
  const key = EnvironmentWriteKey[environment];

  const useSegmentStagingKey = __USE_SEGMENT_STAGING_KEY__ === "true";
  const effectiveKey =
    useSegmentStagingKey && analyticsEnabled ? stagingWriteKey : key;

  if (!effectiveKey || !analyticsEnabled) {
    return noopAnalytics;
  }

  return AnalyticsBrowser.load(
    {
      writeKey: effectiveKey,
    },
    {
      globalAnalyticsKey: "gocardlessAnalytics",
      obfuscate: true,
      // Set custom cookie and localStorage keys as this allows us override any settings from the client app
      user: {
        cookie: {
          key: "gocardless_ajs_user_id",
        },
        localStorage: {
          key: "gocardless_ajs_user_traits",
        },
      },
    }
  );
};

export const attachSegmentAnalytics = (
  bus: EventBus,
  analytics: AnalyticsInstance
): (() => void) =>
  bus.on("analytics:track", ({ event, properties }) => {
    analytics.track(`UI Components - ${event}`, properties);
  });
